import type { ColumnEntry } from "@/v2/framework/column/types";
import type { DynamicColumnSlot } from "@/v2/framework/view/types";
import { FieldType, ColumnIconType } from "@/v2/framework/column/types";
import { playerStatusOptions } from "../shared";
import { t } from "@/services/i18n";
import { getSupplementalsByEntity } from "@/services/supplementals";

type PlayerGridRow = {
  sportCode?: string;
  prematchPosition?: number | string | null;
  livePosition?: number | string | null;
  prematchGame?: { position?: number | string | null };
  liveGame?: { position?: number | string | null };
  status?: number;
  feeds?: { name: string }[];
  supplementalNames?: { supplementalNameTypeId: string; name: string }[];
};

function positionValue(
  row: PlayerGridRow,
  topLevel: keyof PlayerGridRow,
  nested: "prematchGame" | "liveGame",
): string {
  const nestedValue = row[nested]?.position;
  const value = row[topLevel] ?? nestedValue;
  return value == null ? "" : String(value);
}

export const columns: ColumnEntry[] = [
  { key: "id", defaultVisibility: true, title: "table_columns.id", fieldType: FieldType.TextCell },
  {
    key: "syntheticId",
    defaultVisibility: false,
    title: "table_columns.synthetic_id",
    fieldType: FieldType.TextCell,
  },
  {
    key: "sportName",
    defaultVisibility: true,
    title: "table_columns.sport",
    fieldType: FieldType.TextCell,
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => {
        const item = row as PlayerGridRow;
        return { code: `${item.sportCode ?? ""}` };
      },
    },
  },
  {
    key: "name",
    defaultVisibility: true,
    title: "table_columns.name",
    fieldType: FieldType.InputCell,
  },
  {
    key: "sourceName",
    defaultVisibility: true,
    title: "table_columns.source_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "prematchPosition",
    defaultVisibility: true,
    title: "table_columns.prematch_position",
    fieldType: FieldType.InputCell,
    apiPath: "prematchGame.position",
    sort: {
      enabled: true,
      key: "prematchPosition",
    },
    valueFormatter: (row: unknown) => positionValue(row as PlayerGridRow, "prematchPosition", "prematchGame"),
  },
  {
    key: "livePosition",
    defaultVisibility: true,
    title: "table_columns.live_position",
    fieldType: FieldType.InputCell,
    apiPath: "liveGame.position",
    sort: {
      enabled: true,
      key: "livePosition",
    },
    valueFormatter: (row: unknown) => positionValue(row as PlayerGridRow, "livePosition", "liveGame"),
  },
  {
    key: "sourceTenantName",
    defaultVisibility: false,
    title: "table_columns.source_tenant_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "status",
    defaultVisibility: true,
    title: "table_columns.status",
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) => {
      const item = row as PlayerGridRow;
      const status = playerStatusOptions.find((option) => option.value === item.status);
      return t(status?.title ?? "");
    },
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => {
        const item = row as PlayerGridRow;
        const status = playerStatusOptions.find((option) => option.value === item.status);
        return {
          code: status?.icon?.code ?? "",
          color: status?.icon?.color ?? "",
        };
      },
    },
  },
  {
    key: "info",
    defaultVisibility: false,
    title: "table_columns.info",
    fieldType: FieldType.TextCell,
  },
  {
    key: "feeds",
    defaultVisibility: false,
    title: "table_columns.feeds",
    fieldType: FieldType.ArrayCell,
    valueFormatter: (row: unknown) => {
      const item = row as PlayerGridRow;
      return (item.feeds ?? []).map((feed) => {
        const [name] = feed.name?.split(" - ") ?? [];
        return name ?? "";
      });
    },
  },
  {
    key: "gridChrome",
    defaultVisibility: true,
    configurableVisibility: false,
    title: "",
    fieldType: FieldType.ActionsCell,
    sticky: true,
    columnsPanelTitle: "common.labels.column_view",
    headerActions: [
      {
        icon: "table-d",
        label: "Column view",
      },
    ],
    rowActions: [
      {
        icon: "edit-ng",
        label: "Edit",
        onClick: (ctx) => ctx.openDialog("edit"),
      },
      {
        icon: "trash-ng",
        label: "Delete",
        onClick: (ctx) => ctx.openDialog("delete"),
      },
    ],
  },
];

export const dynamicColumnSlots: DynamicColumnSlot[] = [
  {
    anchor: "name",
    createColumns: () => {
      const supplementalNameTypes = getSupplementalsByEntity("PLAYER");

      return supplementalNameTypes.map((item) => ({
        key: item.id,
        title: `table_columns.${item.name.toLowerCase()}`,
        fieldType: FieldType.InputCell,
        defaultVisibility: true,
        valueFormatter: (row: unknown) => {
          const r = row as PlayerGridRow;
          return r.supplementalNames?.find((s) => s.supplementalNameTypeId === item.id)?.name ?? "";
        },
        payloadOutFormatter: (value: unknown) => ({
          supplementalNames: [{ supplementalNameTypeId: item.id, name: value }],
        }),
      }));
    },
  },
];
